import { Component, computed, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthService } from '../../core/auth/auth.service';

function getAccessTokenFromHash(): string | null {
  const raw = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  return params.get('access_token') || params.get('token');
}

function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const newPassword = control.get('newPassword')?.value;
  const confirmPassword = control.get('confirmPassword')?.value;
  if (!newPassword || !confirmPassword) return null;
  return newPassword === confirmPassword ? null : { passwordMismatch: true };
}

@Component({
  selector: 'op-confirm-password-reset-page',
  imports: [
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  template: `
    <div class="auth-wrap">
      <div class="auth-card">
        <div class="auth-brand">
          <div class="auth-brand-mark">E</div>
          <span class="auth-brand-label">Eldara</span>
        </div>

        <h1 class="auth-title">Set a new password</h1>
        <p class="auth-sub">Choose a strong password — at least 8 characters.</p>

        @if (tokenMissing()) {
          <div class="auth-error">
            Reset link is invalid or expired. Please request a new one.
          </div>
        }

        <form [formGroup]="form" (ngSubmit)="submit()" novalidate class="auth-form">
          <mat-form-field appearance="outline" class="full">
            <mat-label>New password</mat-label>
            <input matInput type="password" formControlName="newPassword" autocomplete="new-password" />
            @if (form.controls.newPassword.touched && form.controls.newPassword.hasError('minlength')) {
              <mat-error>At least 8 characters required.</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="full">
            <mat-label>Confirm password</mat-label>
            <input matInput type="password" formControlName="confirmPassword" autocomplete="new-password" />
          </mat-form-field>

          @if (showPasswordMismatch()) {
            <div class="auth-error">Passwords do not match.</div>
          }
          @if (auth.error(); as err) {
            <div class="auth-error">{{ err }}</div>
          }

          <button
            class="primary-green-btn auth-submit-btn"
            type="submit"
            [disabled]="auth.loading() || form.invalid || passwordMismatch() || tokenMissing()"
          >
            @if (auth.loading()) {
              <mat-progress-spinner diameter="18" mode="indeterminate"></mat-progress-spinner>
              Updating…
            } @else {
              Update password
            }
          </button>
        </form>

        <div class="auth-footer">
          <a routerLink="/auth/sign-in" class="auth-link">← Back to sign-in</a>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .auth-wrap {
      min-height: 100vh;
      display: grid;
      place-items: center;
      padding: 24px;
      background: url('/assets/panel_bg_1.png') center / cover fixed;
      background-color: #0c1a2e;
    }
    .auth-card {
      width: min(420px, 100%);
      padding: 40px 36px 32px;
      border-radius: 18px;
      background: rgba(11, 22, 38, 0.86);
      border: 1px solid rgba(71, 99, 133, 0.55);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      --mdc-outlined-text-field-label-text-color: rgba(217, 218, 219, 0.65);
      --mdc-outlined-text-field-input-text-color: #d9dadb;
      --mdc-outlined-text-field-outline-color: rgba(71, 99, 133, 0.65);
      --mdc-outlined-text-field-hover-outline-color: rgba(108, 150, 192, 0.85);
      --mdc-outlined-text-field-focus-outline-color: #4f96dd;
      --mdc-outlined-text-field-focus-label-text-color: #60afff;
      --mdc-outlined-text-field-caret-color: #4f96dd;
      --mdc-outlined-text-field-error-focus-outline-color: #eb6b72;
      --mdc-outlined-text-field-error-label-text-color: #eb6b72;
      --mdc-outlined-text-field-error-outline-color: rgba(235, 107, 114, 0.7);
    }
    .auth-brand {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 28px;
    }
    .auth-brand-mark {
      display: grid;
      place-items: center;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: rgba(37, 99, 235, 0.32);
      border: 1px solid rgba(147, 197, 253, 0.18);
      color: #93c5fd;
      font-size: 16px;
      font-weight: 700;
    }
    .auth-brand-label {
      font-size: 20px;
      font-weight: 700;
      color: #ffffff;
      letter-spacing: -0.01em;
    }
    .auth-title {
      margin: 0 0 6px;
      font-size: 22px;
      font-weight: 700;
      color: var(--op-main-text-color);
      letter-spacing: -0.01em;
    }
    .auth-sub {
      margin: 0 0 28px;
      font-size: 13px;
      color: var(--op-color-grey);
    }
    .auth-form {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .full { width: 100%; }
    .auth-error {
      margin: 2px 0 8px;
      padding: 10px 14px;
      border-radius: 8px;
      background: rgba(235, 107, 114, 0.12);
      border: 1px solid rgba(235, 107, 114, 0.3);
      color: #f28a90;
      font-size: 13px;
    }
    .auth-submit-btn {
      width: 100%;
      margin-top: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    .auth-footer {
      margin-top: 24px;
      text-align: center;
    }
    .auth-link {
      font-size: 13px;
      color: var(--op-color-grey);
      text-decoration: none;
      transition: color 120ms ease;
      &:hover { color: var(--op-main-text-color); }
    }
    .auth-card input.mat-mdc-input-element {
      color: #d9dadb;
      caret-color: #4f96dd;
    }
    .auth-card input:-webkit-autofill,
    .auth-card input:-webkit-autofill:hover,
    .auth-card input:-webkit-autofill:focus {
      -webkit-text-fill-color: #d9dadb;
      -webkit-box-shadow: 0 0 0 1000px #0d1e35 inset;
      box-shadow: 0 0 0 1000px #0d1e35 inset;
      caret-color: #4f96dd;
      transition: background-color 9999s ease-in-out 0s;
    }
  `],
})
export class ConfirmPasswordResetPage {
  readonly auth = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  private readonly recoveryAccessToken = computed(() => {
    const qp =
      this.route.snapshot.queryParamMap.get('access_token') ||
      this.route.snapshot.queryParamMap.get('token');
    if (qp) return qp;
    return getAccessTokenFromHash();
  });

  readonly tokenMissing = computed(() => !this.recoveryAccessToken());

  readonly form = this.fb.nonNullable.group(
    {
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(8)]],
    },
    { validators: [passwordMatchValidator] },
  );

  readonly passwordMismatch = computed(() => this.form.hasError('passwordMismatch'));
  readonly showPasswordMismatch = computed(() => {
    const a = this.form.controls.newPassword;
    const b = this.form.controls.confirmPassword;
    return this.passwordMismatch() && (a.dirty || a.touched || b.dirty || b.touched);
  });

  async submit(): Promise<void> {
    this.auth.clearError();

    const token = this.recoveryAccessToken();
    if (!token) return;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    try {
      await this.auth.confirmPasswordReset(token, this.form.controls.newPassword.value);
      // Remove token from URL so it isn't left in history
      history.replaceState(null, '', window.location.pathname);
      await this.router.navigateByUrl('/auth/sign-in', { replaceUrl: true });
    } catch {
      // handled in AuthService
    }
  }
}
